/**
 * Desempenho dos fornecedores.
 *
 * Forma: tabela, não gráfico. São mais classes do que cor consegue distinguir, e
 * cada linha carrega cinco medidas diferentes — nenhuma delas é a "principal".
 * A barra de taxa de vitória é um reforço visual dentro da célula, no tom de
 * acento único, para o olho varrer a coluna sem ler número por número.
 */

import React from "react";
import { Cartao, SemDados, horas, moeda, percentual } from "./viz";
import type { Insights } from "@utils/planilha-insights";

export const DesempenhoFornecedores: React.FC<{ insights: Insights }> = ({
  insights,
}) => {
  if (insights.desempenho.length === 0) {
    return (
      <Cartao titulo="Desempenho dos fornecedores">
        <SemDados>
          Nenhum fornecedor associado aos itens desta planilha.
        </SemDados>
      </Cartao>
    );
  }

  return (
    <Cartao
      titulo="Desempenho dos fornecedores"
      descricao="Quem disputou, quem venceu e quanto tempo levou para responder."
    >
      <div className="overflow-x-auto">
        <table className="table table-sm">
          <thead>
            <tr>
              <th>Fornecedor</th>
              <th className="text-center">Disputou</th>
              <th className="text-center">Venceu</th>
              <th>Taxa de vitória</th>
              <th className="text-right">Valor ganho</th>
              <th className="text-right">Resposta</th>
            </tr>
          </thead>
          <tbody>
            {insights.desempenho.map((d) => (
              <tr key={d.nome}>
                <td className="max-w-60">
                  <span className="block truncate font-medium" title={d.nome}>
                    {d.nome}
                  </span>
                  <span className="flex flex-wrap gap-1 text-xs text-base-content/60">
                    {d.recusas > 0 && (
                      <span>
                        {d.recusas} {d.recusas === 1 ? "recusa" : "recusas"}
                      </span>
                    )}
                    {d.nuncaAbriu && (
                      <span className="text-warning">· nunca abriu o link</span>
                    )}
                  </span>
                </td>
                <td className="text-center tabular-nums">{d.disputas}</td>
                <td className="text-center tabular-nums">{d.vitorias}</td>
                <td className="min-w-[120px]">
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className="h-1.5 flex-1 overflow-hidden rounded-full"
                      style={{ background: "var(--viz-grid)" }}
                    >
                      <span
                        className="block h-full rounded-full"
                        style={{
                          width: `${d.taxaVitoria * 100}%`,
                          background: "var(--viz-accent)",
                        }}
                      />
                    </span>
                    <span className="w-9 shrink-0 text-right text-xs tabular-nums text-base-content/70">
                      {percentual(d.taxaVitoria)}
                    </span>
                  </span>
                </td>
                <td className="text-right tabular-nums">
                  {d.valorGanho > 0 ? moeda(d.valorGanho) : "—"}
                </td>
                <td className="text-right tabular-nums">
                  {d.horasMedias !== undefined ? horas(d.horasMedias) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Cartao>
  );
};
